import axios, { AxiosRequestConfig } from 'axios';
import TokenService from './token.service';
import ApiHandler from '@/utils/api.handler';
import { FileDataInterface } from '@/store/interfaces/filedata.interface';

export default class ApiService {

  /**
   * Initialize the base URL against axios.
   * @param baseURL The API base URL
   */
  public static init(baseURL: string) {
    const service = new ApiHandler();
    axios.defaults.baseURL = baseURL;
    axios.defaults.withCredentials = true; // enables passing of cookies
    axios.interceptors.request.use((requestConfig: AxiosRequestConfig) => service.requestHandler(requestConfig));
    axios.interceptors.response.use(
      (responseConfig) => service.successHandler(responseConfig),
      (error) => service.errorHandler(error),
    );
  }

  /**
   * Set the header with key and content if any, otherwise set Authorization header by default
   * @param {string} key The header key entry
   * @param {string} content The content for the key entry
   */
  public static setHeader(key?: string, content?: string): void {
    if (!!key && !!content) {
      axios.defaults.headers.common[key] = content;
      return;
    }
    const k = 'Authorization';
    axios.defaults.headers.common[k] = `Bearer ${TokenService.getToken()}`;
  }

  /**
   * Remove the header specified by the key if any, otherwise remove all headers
   * @param {string} key The header key entry
   */
  public static removeHeader(key?: string): void {
    if (!!key) {
      delete axios.defaults.headers.common[key];
      return;
    }
    axios.defaults.headers.common = {};
  }

  /**
   * Get the resource
   * @param resource The API resource to get
   */
  public static get(resource: string, config?: AxiosRequestConfig) {
    return axios.get(resource, config);
  }

  /**
   * Post to the API resource
   * @param resource The API resource where to post
   * @param data The payload as JSON object
   */
  public static post(resource: string, data: object) {
    return axios.post(resource, data);
  }

  /**
   * Replace a resource
   * @param resource The API resource to update
   * @param data The payload as JSON object
   */
  public static put(resource: string, data: object) {
    return axios.put(resource, data);
  }

  /**
   * Update/patch a resource
   * @param resource The API resource to update
   * @param data The payload as JSON object
   */
  public static patch(resource: string, data: object) {
    return axios.patch(resource, data);
  }

  /**
   * Delete a resource
   * @param resource The API resource to delete
   */
  public static delete(resource: string) {
    return axios.delete(resource);
  }

  /**
   * Perform a custom Axios request.
   *
   * data is an object containing the following properties:
   *  - method
   *  - url
   *  - data ... request payload
   *  - auth (optional)
   *    - username
   *    - password
   * @param data
   */
  public static async customRequest(data: AxiosRequestConfig) {
    return axios(data);
  }

  /**
   * Create a new instance of axios without interceptors. Use it for special cases only!
   */
  public static async create() {
    const instance = axios.create();
    instance.defaults.baseURL = process.env.VUE_APP_PUBLIC_ROOT_API;
    instance.defaults.withCredentials = true; // enables passing of cookies

    return instance;
  }

  /**
   * get a file from backend
   * @param url The url where is the file
   * @param params The params
   * @param defaultName The default name in case of the filename is not provided by the API
   */
  public static async getFileFromBackend(url: string
                                       , params?: { [key: string]: string | string[] }
                                       , defaultName?: string
                                       , handlerSuccessEnabled: boolean = true): Promise<FileDataInterface> {
    let paramsUrl: string = '';
    if (params) {
      for (const key in params) {
        if (params.hasOwnProperty(key)) {
          if (paramsUrl === '') {
            paramsUrl += '?';
          } else {
            paramsUrl += '&';
          }
          const value = params[key];
          if (Array.isArray(value)) {
            for (const innerValue of value) {
              paramsUrl += `${key}=${innerValue}`;
            }
          } else {
            paramsUrl += `${key}=${value}`;
          }
        }
      }
    }

    return axios.get(url + paramsUrl,
      { responseType: 'blob', handlerSuccessEnabled } as AxiosRequestConfig).then(
      (res: any) => {
        const contentDispositionHeader = res.headers['content-disposition'];
        let name = defaultName;
        if (contentDispositionHeader) {
          name = contentDispositionHeader.split(';')[1].trim().split('=')[1].replace(/"/g, '');
        }
        res.name = name;

        return {
          blob: res.data,
          name: res.name,
        };
      },
    );
  }
}
